import React from "react";
import { GROUP_COLORS } from "./benchmarkData";

/**
 * Horizontal bar chart, hand-rolled to match the site's existing CSS
 * custom-property bar idiom (see Demo/lab.css .emotion-meter). Group
 * dividers communicate grouping so it isn't color-alone (WCAG 1.4.1).
 */
export function BarChart({ rows, unit = "%" }) {
    const maxValue = Math.max(...rows.map((row) => row.value)) * 1.12;
    let lastGroup = null;

    return (
        <div className="benchmark-bar-chart">
            {rows.map((row) => {
                const showDivider = row.group !== lastGroup;
                lastGroup = row.group;
                const colors = GROUP_COLORS[row.group];
                const pct = (row.value / maxValue) * 100;

                return (
                    <React.Fragment key={row.label}>
                        {showDivider && (
                            <div className="benchmark-group-divider">{colors.label}</div>
                        )}
                        <div className={`benchmark-bar-row ${row.group === "aihumanity" ? "is-aihumanity" : ""}`}>
                            <span className="benchmark-bar-label">{row.label}</span>
                            <div className="benchmark-bar-track">
                                <i
                                    className="benchmark-bar-fill"
                                    style={{ "--fill": `${pct}%`, "--color": colors.fill, "--color-end": colors.fillEnd }}
                                />
                            </div>
                            <strong className="benchmark-bar-value">{row.value.toFixed(1)}{unit}</strong>
                        </div>
                    </React.Fragment>
                );
            })}
        </div>
    );
}

function niceTicks(min, max, count) {
    const step = (max - min) / (count - 1);
    return Array.from({ length: count }, (_, i) => Math.round(min + step * i));
}

/**
 * Speed/accuracy scatter — small inline SVG since 2D positioning isn't
 * expressible with the CSS-width bar trick.
 */
export function ScatterChart({ points }) {
    const width = 560;
    const height = 400;
    const padding = { top: 24, right: 28, bottom: 52, left: 52 };
    const plotWidth = width - padding.left - padding.right;
    const plotHeight = height - padding.top - padding.bottom;

    const maxLatency = Math.max(...points.map((p) => p.latencyMs)) * 1.15;
    const minValue = Math.min(...points.map((p) => p.value)) - 6;
    const maxValue = Math.max(...points.map((p) => p.value)) + 6;

    const xScale = (lat) => padding.left + (lat / maxLatency) * plotWidth;
    const yScale = (val) => padding.top + plotHeight - ((val - minValue) / (maxValue - minValue)) * plotHeight;

    const xTicks = niceTicks(0, maxLatency, 5);
    const yTicks = niceTicks(minValue, maxValue, 5);

    return (
        <svg viewBox={`0 0 ${width} ${height}`} className="benchmark-scatter" role="img" aria-label="Latency versus accuracy scatter plot">
            {yTicks.map((tick) => (
                <g key={`y-${tick}`}>
                    <line x1={padding.left} x2={width - padding.right} y1={yScale(tick)} y2={yScale(tick)} className="benchmark-scatter-grid" />
                    <text x={padding.left - 10} y={yScale(tick)} className="benchmark-scatter-axis-label" textAnchor="end" dominantBaseline="middle">
                        {tick}%
                    </text>
                </g>
            ))}
            {xTicks.map((tick) => (
                <g key={`x-${tick}`}>
                    <text x={xScale(tick)} y={height - padding.bottom + 22} className="benchmark-scatter-axis-label" textAnchor="middle">
                        {tick}
                    </text>
                </g>
            ))}
            <text x={width / 2} y={height - 8} className="benchmark-scatter-axis-title" textAnchor="middle">
                Latency (ms/image) — lower is faster
            </text>
            <text
                x={-(height / 2)}
                y={16}
                className="benchmark-scatter-axis-title"
                textAnchor="middle"
                transform="rotate(-90)"
            >
                Accuracy — higher is better
            </text>

            {points.map((point) => {
                const colors = GROUP_COLORS[point.group];
                const isAih = point.group === "aihumanity";
                const cx = xScale(point.latencyMs);
                const cy = yScale(point.value);
                return (
                    <g key={point.label}>
                        {isAih && <circle cx={cx} cy={cy} r={13} className="benchmark-scatter-glow" />}
                        <circle cx={cx} cy={cy} r={isAih ? 7 : 5} fill={colors.fill} className={isAih ? "benchmark-scatter-point is-aihumanity" : "benchmark-scatter-point"} />
                        <text x={cx + (isAih ? 14 : 9)} y={cy + 4} className={isAih ? "benchmark-scatter-point-label is-aihumanity" : "benchmark-scatter-point-label"}>
                            {point.label.replace(" ONNX", "").replace("AIHumanity ", "")}
                        </text>
                    </g>
                );
            })}
        </svg>
    );
}
